import { useEffect, useState, ChangeEvent } from "react";
import clsx from "clsx";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { RootState } from "../../state/configureStore";
import FirebaseService from "../../services/firebase/FirebaseService";
import StandardModal from "./StandardModal";
import ModalSize from "./ModalSize";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { FormControl, FormControlLabel, FormGroup, useMediaQuery, Checkbox, Tooltip } from "@material-ui/core";
import { ProviderContext, withSnackbar } from "notistack";
import Classroom from "../../interfaces/Classroom";
import AssignmentOnTeacher from "../../state/teacherData/AssignmentOnTeacher";
import ReadleeProfile from "../../services/firebase/ReadleeProfile";
import ClassroomAssignmentDetails from "../../interfaces/ClassroomAssignmentDetails";
import { useSnackbar } from "./modalHooks";
import { ReadleeColors } from "../../themes/default";
import "./AssignModal.scss";

const useStyles = makeStyles({
  root: {
    "&:hover": {
      backgroundColor: "transparent !important",
    },
  },
  icon: {
    borderRadius: 3,
    boxShadow: "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
    backgroundColor: "#f5f8fa",
    backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))",
    "$root.Mui-focusVisible &": {
      outline: "2px auto rgba(19,124,189,.6)",
      outlineOffset: 2,
    },
    "input:hover ~ &": {
      backgroundColor: "white",
    },
    "input:disabled ~ &": {
      boxShadow: "none",
      background: "rgba(206,217,224,.5)",
    },
  },
  checkedIcon: {
    backgroundColor: "white",
    backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
    "&:before": {
      display: "block",
      backgroundImage:
        "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
        " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
        "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%3a3e4c'/%3E%3C/svg%3E\")",
      content: '""',
    },
  },
  classIconSize: {
    width: 22,
    height: 22,
    "@media only screen and (max-width: 600px)": {
      width: 30,
      height: 30,
    },
  },
  studentIconSize: {
    width: 17,
    height: 17,
    "@media only screen and (max-width: 600px)": {
      width: 20,
      height: 20,
    },
  },

  classCheckedIconSize: {
    "&:before": {
      width: 22,
      height: 22,
      "@media only screen and (max-width: 600px)": {
        width: 30,
        height: 30,
      },
    },
  },
  studentCheckedIconSize: {
    "&:before": {
      width: 17,
      height: 17,
      "@media only screen and (max-width: 600px)": {
        width: 20,
        height: 20,
      },
    },
  },
});

interface CustomCheckboxProps {
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  name: string;
  isStudentCheckbox?: boolean;
}

const CustomCheckbox = ({ checked, onChange, name, isStudentCheckbox = false }: CustomCheckboxProps) => {
  const classes = useStyles();
  return (
    <Checkbox
      className={classes.root}
      disableRipple
      checkedIcon={
        <span
          className={clsx(
            classes.icon,
            classes.checkedIcon,
            isStudentCheckbox ? classes.studentCheckedIconSize : classes.classCheckedIconSize,
          )}
        />
      }
      icon={
        <span className={`${classes.icon} ${isStudentCheckbox ? classes.studentIconSize : classes.classIconSize}`} />
      }
      inputProps={{ "aria-label": "decorative checkbox" }}
      checked={checked}
      onChange={onChange}
      name={name}
    />
  );
};

interface AssignModalModalProps {
  assignmentId: string;
  classrooms: Classroom[];
  students: Students;
  closeModal: () => void;
  open: boolean;
  isLoadingTeacher: boolean;
  assignments: Assignments;
  assignmentClasses: AssignmentClasses;
  userId: string;
}

interface Students {
  [key: string]: ReadleeProfile;
}

interface AssignmentClasses {
  [key: string]: {
    [key: string]: ClassroomAssignmentDetails;
  };
}

interface Assignments {
  [key: string]: AssignmentOnTeacher;
}

// Returns object of student readlee profiles (key is the student id) of only students in the class
const filterStudents = (classroom: Classroom, students: Students) =>
  // If classroom doesn't exist or there are no students
  !classroom?.students
    ? ({} as Students)
    : Object.entries(students).reduce(
        (acc, [key, student]) => (classroom.students[key] ? { ...acc, [key]: student } : acc),
        {},
      );

const AssignModal = ({
  assignmentId,
  open,
  closeModal,
  classrooms,
  students,
  assignments,
  assignmentClasses,
  isLoadingTeacher,
  userId,
}: AssignModalModalProps & ProviderContext) => {
  const [assignedClassrooms, setAssignedClassrooms] = useState<Map<Classroom, ReadleeProfile[]>>(new Map());
  const [selectAll, setSelectAll] = useState(false);
  const [displayedClassroom, setDisplayedClassroom] = useState<Classroom>();
  const [displayedStudents, setDisplayedStudents] = useState<Array<ReadleeProfile>>();
  const [reset, setReset] = useState(false);

  const enqueueSnackbar = useSnackbar();

  useEffect(() => {
    if (!isLoadingTeacher || reset) {
      const newMap = new Map();
      const assignmentClass = assignmentClasses[assignmentId];
      classrooms.forEach(classroom => {
        const assignmentDetails = assignmentClass && assignmentClass[classroom.id];
        if (assignmentDetails) {
          const studentList: ReadleeProfile[] = Object.values(filterStudents(classroom, students));
          const assignedStudents: ReadleeProfile[] = [];
          studentList.forEach(student => {
            if (assignmentDetails.students[student.uid]) {
              assignedStudents.push(student);
            }
          });
          newMap.set(classroom, assignedStudents);
        }
      });
      setAssignedClassrooms(new Map(newMap));
      setReset(false);
    }

    if (classrooms[0]) {
      setDisplayedStudents(Object.values(filterStudents(classrooms[0], students)));
      setDisplayedClassroom(classrooms[0]);
    }
  }, [classrooms, students, assignmentId, isLoadingTeacher, assignmentClasses, reset]);

  const close = () => {
    setReset(true);
    closeModal();
  };

  const save = async () => {
    try {
      const assignmentClassDetails = assignmentClasses[assignmentId];
      classrooms.forEach(async classroom => {
        const assignmentDetails = assignmentClassDetails && assignmentClassDetails[classroom.id];
        const studentList: ReadleeProfile[] = Object.values(filterStudents(classroom, students));
        const currClassStudents = assignedClassrooms.get(classroom);
        if (currClassStudents) {
          if (assignments[assignmentId]?.assignmentDetails?.isActive === false) {
            await FirebaseService.updateAssignmentIsActive(userId, assignmentId, true);
          }
          if (!assignmentDetails) {
            await FirebaseService.addAssignmentToClass(assignmentId, classroom.id);
            studentList.forEach(async student => {
              const currStudent = currClassStudents.find(s => s === student);
              if (!currStudent) {
                await FirebaseService.manageStudentOnAssignment(student.uid, classroom.id, assignmentId, false);
              }
            });
          } else {
            studentList.forEach(async student => {
              const currClassStudent = currClassStudents.find(s => s === student);
              if (currClassStudent && assignmentDetails.students[student.uid] === false)
                await FirebaseService.manageStudentOnAssignment(student.uid, classroom.id, assignmentId, true);
              else if (!currClassStudent && assignmentDetails.students[student.uid] === true)
                await FirebaseService.manageStudentOnAssignment(student.uid, classroom.id, assignmentId, false);
            });
          }
        } else {
          if (assignmentDetails) {
            await FirebaseService.deleteAssignmentFromClass(assignmentId, classroom.id);
          }
        }
      });

      enqueueSnackbar(`Assignment Saved`, "success");
      closeModal();
    } catch (e) {
      enqueueSnackbar("Could not update this assignment", "error");
    }
  };

  const updateMap = (k: Classroom, v: ReadleeProfile[]) => {
    setAssignedClassrooms(new Map(assignedClassrooms.set(k, v)));
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const viewClass = (classroom: Classroom) => {
    const studentList = Object.values(filterStudents(classroom, students));
    setDisplayedStudents(studentList);
    setDisplayedClassroom(classroom);
    const selectedStudents = assignedClassrooms.get(classroom);
    if (selectedStudents) {
      const allChecked = studentList.length !== 0 && studentList.every(s => selectedStudents.includes(s));
      setSelectAll(allChecked);
    } else setSelectAll(false);
  };

  const assignStudent = (student: ReadleeProfile) => () => {
    if (displayedClassroom) {
      const selectedStudents = assignedClassrooms.get(displayedClassroom);

      if (selectedStudents) {
        if (selectedStudents.includes(student)) {
          selectedStudents.splice(selectedStudents.indexOf(student), 1);
          if (selectedStudents.filter(s => displayedStudents?.includes(s)).length === 0) {
            assignedClassrooms.delete(displayedClassroom);
          }
          setSelectAll(false);
          setAssignedClassrooms(new Map(assignedClassrooms));
        } else {
          selectedStudents.push(student);
          updateMap(displayedClassroom, selectedStudents);
        }
      } else {
        updateMap(displayedClassroom, [student]);
      }
    }
  };

  const assignClass = (classroom: Classroom, checked: boolean) => {
    const studentList = Object.values(filterStudents(classroom, students));

    if (checked) {
      updateMap(classroom, studentList);
    } else {
      assignedClassrooms.delete(classroom);
    }

    viewClass(classroom);
    setSelectAll(checked);
  };

  const assignAll = () => {
    if (displayedClassroom) {
      const studentList = Object.values(filterStudents(displayedClassroom, students));

      if (selectAll) {
        assignedClassrooms.delete(displayedClassroom);
      } else {
        updateMap(displayedClassroom, studentList);
      }

      setSelectAll(!selectAll);
    }
  };

  const removeClass = () => {
    if (displayedClassroom) {
      assignedClassrooms.delete(displayedClassroom);
      setAssignedClassrooms(new Map(assignedClassrooms));
      setSelectAll(false);
    }
  };

  return (
    <StandardModal
      title="Select students to share assignment with"
      submitButtonText="save"
      size={fullScreen ? ModalSize.fullScreen : ModalSize.large}
      onlyOneButton={false}
      open={open}
      cancelClose={close}
      onSubmit={save}
      className="AssignModal"
      key="assign"
    >
      <div className="body">
        <div className="classes">
          <FormControl component="fieldset" style={{ width: "100%" }}>
            <FormGroup>
              {displayedClassroom ? (
                <>
                  {classrooms.map(classroom => {
                    return (
                      <div key={classroom.id}>
                        <div className={`class-container ${displayedClassroom?.id === classroom.id && "selected"}`}>
                          <FormControlLabel
                            control={
                              <CustomCheckbox
                                checked={assignedClassrooms.has(classroom)}
                                onChange={e => assignClass(classroom, e.target.checked)}
                                name={classroom.id}
                              />
                            }
                            label={<></>}
                            style={{ margin: "0px" }}
                          />
                          <button className="class-button" onClick={() => viewClass(classroom)}>
                            <span className="label">{classroom.className}</span>
                            {!fullScreen && (
                              <ArrowForwardIosIcon
                                style={{
                                  fontSize: "12px",
                                  marginRight: "20px",
                                }}
                              />
                            )}
                          </button>
                        </div>
                        <hr className="line" />
                      </div>
                    );
                  })}
                </>
              ) : (
                <div>There are no classrooms</div>
              )}
            </FormGroup>
          </FormControl>
        </div>
        <div className="students">
          {displayedStudents ? (
            <>
              {displayedStudents.length !== 0 ||
              (displayedClassroom?.preRegisteredStudents
                ? displayedClassroom.preRegisteredStudents.length !== 0
                : false) ? (
                <>
                  <div className="all">
                    <FormControlLabel
                      control={
                        <CustomCheckbox
                          checked={selectAll}
                          onChange={assignAll}
                          name={"All Students"}
                          isStudentCheckbox
                        />
                      }
                      label={<span className="all-text">All Students</span>}
                      style={{ margin: "0px" }}
                    />

                    <button className="remove" onClick={removeClass}>
                      <DeleteOutlineIcon
                        style={{
                          color: ReadleeColors.accentRed,
                        }}
                        className="trash"
                      />
                      Remove all
                    </button>
                  </div>
                  <div className="student-list">
                    <FormControl component="fieldset" style={{ width: "100%" }}>
                      {displayedStudents.map(student => {
                        const uncheckedStudentList = displayedClassroom && assignedClassrooms.get(displayedClassroom);
                        const checkedStudentList = uncheckedStudentList ? uncheckedStudentList : [];
                        return (
                          <FormControlLabel
                            key={student.uid}
                            control={
                              <CustomCheckbox
                                checked={checkedStudentList.includes(student)}
                                onChange={assignStudent(student)}
                                name={student.uid}
                                key={student.uid}
                                isStudentCheckbox
                              />
                            }
                            label={
                              <span className="label student-label">
                                {student.firstName} {student.lastName}
                              </span>
                            }
                            style={{ margin: "0px" }}
                          />
                        );
                      })}
                      {displayedClassroom?.preRegisteredStudents?.map(preRegisteredStudent => {
                        return (
                          <Tooltip
                            title={
                              assignedClassrooms.has(displayedClassroom)
                                ? "This student is pre-registered. When they sign in they will automatically be added to this assignment."
                                : "This student is pre-registered. Once they sign in they will see any readings assigned to this full class."
                            }
                          >
                            <FormControlLabel
                              key={preRegisteredStudent}
                              control={
                                <CustomCheckbox
                                  checked={assignedClassrooms.has(displayedClassroom)}
                                  onChange={() => {}}
                                  name={preRegisteredStudent}
                                  key={preRegisteredStudent}
                                  isStudentCheckbox
                                />
                              }
                              label={<span className="label student-label">{preRegisteredStudent}</span>}
                              style={{ margin: "0px" }}
                              disabled
                            />
                          </Tooltip>
                        );
                      })}
                    </FormControl>
                  </div>
                </>
              ) : (
                <div className="empty-class">{displayedClassroom?.className} has no students.</div>
              )}
            </>
          ) : (
            <div>There are no students to view</div>
          )}
        </div>
      </div>
    </StandardModal>
  );
};

const mapStateToProps = (
  state: RootState,
): {
  classrooms: Classroom[];
  students: Students;
  isLoadingTeacher: boolean;
  assignmentClasses: AssignmentClasses;
  assignments: Assignments;
  userId: string;
} => ({
  classrooms: state.teacherState.classrooms,
  students: state.teacherState.students,
  isLoadingTeacher: state.teacherState.isLoadingTeacher,
  assignmentClasses: state.teacherState.assignmentClasses,
  assignments: state.teacherState.assignments,
  userId: state.currentUserState.currentUser?.uid || "",
});

export default connect(mapStateToProps)(withSnackbar(AssignModal));
